import React, { useState, useEffect } from 'react';
import './Formulario.css';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';
import { mensagemErroDocumento } from '../utils/validarDocumento';

function FormularioCliente({ cliente, onSalvar, onCancelar }) {
  const camposIniciais = {
    id: '',
    dataCriacao: '',
    nome: '',
    celular: '',
    email: '',
    endereco: '',
    numero: '',
    data_nasc: '',
    cidade: '',
    uf: '',
    bairro: '',
    cep: '',
    cpF_CNPJ: '',
    rg: '',
    regUnico: '',
    tipoPessoaId: null
  };

  const [valores, setValores] = useState(camposIniciais);
  const [botaoDesabilitado, setBotaoDesabilitado] = useState(false);
  const [mostrarSucesso, setMostrarSucesso] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [erroCpfCnpj, setErroCpfCnpj] = useState('');

  // Função para fazer chamadas à API com fallback de URLs
  const apiCall = async (endpoint, method = 'GET', data = null) => {
    const urls = [
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`
    ];

    for (const url of urls) {
      try {
        console.log(`Tentando ${method}: ${url}`);
        
        const options = {
          method,
          headers: {
            'Content-Type': 'application/json',
          }
        };
        const token = getAuthToken();
        if (token) options.headers['Authorization'] = 'Bearer ' + token;
        
        if (data && method !== 'GET') {
          options.body = JSON.stringify(data);
        }
        
        const response = await fetch(url, options);
        if (response.ok) {
          console.log(`Sucesso ${method} com ${url}`);
          return method === 'GET' ? await response.json() : response;
        }
      } catch (error) {
        console.log(`Falha ${method} com ${url}:`, error.message);
      }
    }
    
    throw new Error(`Todas as URLs falharam para ${method} ${endpoint}`);
  };

  useEffect(() => {
    if (cliente) {
      console.log('Cliente recebido no formulário:', cliente);
      
      // Função para converter data para formato YYYY-MM-DD
      const formatarDataParaInput = (data) => {
        if (!data) return '';
        try {
          const date = new Date(data);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        } catch {
          return '';
        }
      };
      
      const cpfCnpjValue = cliente.cpF_CNPJ || cliente.CPF_CNPJ || cliente.cpf_cnpj || cliente.CpfCnpj || '';
      setValores({
        id: cliente.id || cliente.Id || cliente.ID || '',
        dataCriacao: formatarDataParaInput(cliente.dataCriacao || cliente.DataCriacao || cliente.data_criacao),
        nome: cliente.nome || cliente.Nome || cliente.NOME || '',
        celular: cliente.celular || cliente.Celular || cliente.CELULAR || '',
        email: cliente.email || cliente.Email || cliente.EMAIL || '',
        endereco: cliente.endereco || cliente.Endereco || cliente.ENDERECO || '',
        numero: cliente.numero || cliente.Numero || cliente.NUMERO || '',
        data_nasc: formatarDataParaInput(cliente.data_nasc || cliente.DataNasc || cliente.Data_Nasc || cliente.DATA_NASC),
        cidade: cliente.cidade || cliente.Cidade || cliente.CIDADE || '',
        uf: cliente.uf || cliente.UF || cliente.Uf || '',
        bairro: cliente.bairro || cliente.Bairro || cliente.BAIRRO || '',
        cep: cliente.cep || cliente.CEP || cliente.Cep || '',
        cpF_CNPJ: cpfCnpjValue,
        rg: cliente.rg || cliente.RG || cliente.Rg || '',
        regUnico: cliente.regUnico || cliente.RegUnico || cliente.reg_unico || cliente.REG_UNICO || '',
        tipoPessoaId: cliente.tipoPessoaId || cliente.TipoPessoaId || cliente.tipo_pessoa_id || null
      });
    } else {
      // Novo cliente - resetar campos
      setValores(camposIniciais);
    }
    setErroCpfCnpj('');
  }, [cliente]);

  const getTipoDocumentoPorTipoPessoa = () => {
    return valores.tipoPessoaId == 2 ? 'CNPJ' : 'CPF';
  };

  const getCpfCnpjConfig = () => {
    const tipoDocumento = getTipoDocumentoPorTipoPessoa();
    if (tipoDocumento === 'CPF') {
      return { label: 'CPF', maxLength: 11, placeholder: 'Digite o CPF (somente números)' };
    }
    return { label: 'CNPJ', maxLength: 14, placeholder: 'Digite o CNPJ (somente números)' };
  };

  // Buscar endereço pelo CEP usando ViaCEP
  const buscarCep = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    setBuscandoCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();

      if (data.erro) {
        alert('CEP não encontrado. Verifique o CEP digitado.');
        return;
      }

      setValores(prev => ({
        ...prev,
        endereco: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        uf: data.uf || '',
      }));
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      alert('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Tratamento especial para mudança de tipo de documento (CPF/CNPJ)
    // Tratamento especial para mudança de tipo de pessoa
    if (name === 'tipoPessoaId') {
      const novoValor = value;
      const tipoDocumento = novoValor === '2' ? 'CNPJ' : 'CPF';

      setValores((prev) => ({
        ...prev,
        [name]: novoValor,
        cpF_CNPJ: prev.cpF_CNPJ ? prev.cpF_CNPJ.slice(0, tipoDocumento === 'CPF' ? 11 : 14) : ''
      }));
      return;
    }
    
    // Tratamento especial para CPF/CNPJ
    if (name === 'cpF_CNPJ') {
      const config = getCpfCnpjConfig();
      // Remove caracteres não numéricos e limita o tamanho
      const apenasNumeros = value.replace(/\D/g, '');
      const valorLimitado = apenasNumeros.slice(0, config.maxLength);
      setValores((prev) => ({ ...prev, [name]: valorLimitado }));
      setErroCpfCnpj(mensagemErroDocumento(valorLimitado, config.label));
      return;
    }
    
    setValores((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Se o botão está desabilitado, não faz nada
    if (botaoDesabilitado) {
      return;
    }

    // Validações de campos obrigatórios
    if (!valores.nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    if (!valores.celular.trim()) {
      alert('Celular é obrigatório');
      return;
    }

    if (!valores.email.trim()) {
      alert('E-mail é obrigatório');
      return;
    }

    if (!valores.cpF_CNPJ.trim()) {
      const msg = `${getCpfCnpjConfig().label} é obrigatório`;
      setErroCpfCnpj(msg);
      alert(msg);
      return;
    }

    // Validar tamanho e dígitos verificadores do CPF/CNPJ conforme tipo de pessoa
    const config = getCpfCnpjConfig();
    const msgDocumento = mensagemErroDocumento(valores.cpF_CNPJ, config.label);
    if (msgDocumento) {
      setErroCpfCnpj(msgDocumento);
      alert(msgDocumento);
      return;
    }

    if (!valores.tipoPessoaId) {
      alert('Tipo de pessoa é obrigatório');
      return;
    }

    // Campos de endereço obrigatórios
    if (!valores.cep.trim()) {
      alert('CEP é obrigatório');
      return;
    }

    if (!valores.endereco.trim()) {
      alert('Endereço (Rua) é obrigatório');
      return;
    }

    if (!valores.numero.trim()) {
      alert('Número é obrigatório');
      return;
    }

    if (!valores.bairro.trim()) {
      alert('Bairro é obrigatório');
      return;
    }

    if (!valores.cidade.trim()) {
      alert('Cidade é obrigatória');
      return;
    }

    if (!valores.uf.trim()) {
      alert('UF é obrigatória');
      return;
    }

    // Validações de formato (mantendo as existentes)
    if (valores.email && !/\S+@\S+\.\S+/.test(valores.email)) {
      alert('E-mail inválido');
      return;
    }

    if (valores.celular && !/^\d{11}$/.test(valores.celular)) {
      alert('Celular deve conter 11 dígitos');
      return;
    }

    // Montar o payload conforme o modelo do backend
    const payload = {
      nome: valores.nome,
      celular: valores.celular,
      email: valores.email,
      endereco: valores.endereco,
      numero: valores.numero,
      data_nasc: valores.data_nasc,
      cidade: valores.cidade,
      uf: valores.uf,
      bairro: valores.bairro,
      cep: valores.cep,
      cpF_CNPJ: valores.cpF_CNPJ,
      rg: valores.rg,
      regUnico: valores.regUnico,
      tipoPessoaId: valores.tipoPessoaId ? parseInt(valores.tipoPessoaId) : null
    };

    // Se é edição, incluir id e dataCriacao
    if (valores.id) {
      payload.id = parseInt(valores.id);
      payload.dataCriacao = valores.dataCriacao;
    } else {
      payload.dataCriacao = new Date().toISOString().split('T')[0];
    }

    try {
      let response;

      console.log('Payload sendo enviado:', payload);

      if (valores.id) {
        // Editar cliente existente
        console.log(`Editando cliente ID: ${valores.id}`);
        response = await apiCall(`Cliente/${valores.id}`, 'PUT', payload);
      } else {
        // Criar novo cliente
        response = await apiCall('Cliente/CriarCliente', 'POST', payload);
      }

      if (!response || !response.ok) {
        const errorText = response ? await response.text() : 'Erro de conexão';
        throw new Error(errorText || 'Erro ao salvar cliente');
      }

      // ✅ SUCESSO - Para criar novo cliente
      if (!valores.id) {
        console.log('✅ Cliente criado com sucesso!');
        
        // Mostrar notificação de sucesso
        setMostrarSucesso(true);
        
        // Chamar callback se fornecido
        if (onSalvar) {
          onSalvar(payload);
        }

        // Fechar o popup após 2.5 segundos (tempo da animação)
        setTimeout(() => {
          if (onCancelar) {
            onCancelar(); // Fecha o popup
          }
        }, 2500);

        // Remover a notificação após 2 segundos + 0.5s de animação
        setTimeout(() => {
          setMostrarSucesso(false);
        }, 2500);
        
      } else {
        // Para edição, manter comportamento original
        const mensagem = 'Cliente atualizado com sucesso!';
        alert(mensagem);
        
        if (onSalvar) {
          onSalvar(payload);
        }
        
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
      
    } catch (error) {
      console.error('❌ Erro completo:', error);
      
      // Mostrar mensagem de erro
      alert(`Erro ao salvar cliente: ${error.message}`);
      
      // Desabilitar botão por 5 segundos
      setBotaoDesabilitado(true);
      setTimeout(() => {
        setBotaoDesabilitado(false);
      }, 5000);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="formulario-cliente-wrapper" style={{
      backgroundColor: '#fff',
      padding: '30px',
      borderRadius: '10px',
      maxWidth: '800px',
      margin: '0 auto',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)'
    }}>
      <div className="formulario-cliente-grid" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
      }}>
        {/* ID - só mostrar se existir */}
        {valores.id && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="id" style={{ marginBottom: '5px', fontWeight: '500' }}>ID</label>
            <input
              id="id"
              type="number"
              name="id"
              value={valores.id}
              placeholder="ID do cliente"
              onChange={handleChange}
              disabled
              style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px', backgroundColor: '#f5f5f5' }}
            />
          </div>
        )}
        
        {/* Data de Criação - só mostrar se existir */}
        {valores.dataCriacao && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="dataCriacao" style={{ marginBottom: '5px', fontWeight: '500' }}>Data de Criação</label>
            <input
              id="dataCriacao"
              type="date"
              name="dataCriacao"
              value={valores.dataCriacao}
              onChange={handleChange}
              disabled
              style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px', backgroundColor: '#f5f5f5' }}
            />
          </div>
        )}
        
        {/* Nome */}
        <div style={{ display: 'flex', flexDirection: 'column', gridColumn: valores.id ? 'span 2' : 'span 2' }}>
          <label htmlFor="nome" style={{ marginBottom: '5px', fontWeight: '500' }}>Nome *</label>
          <input
            id="nome"
            type="text"
            name="nome"
            className="formulario-cliente-input"
            value={valores.nome}
            placeholder="Digite o nome completo"
            onChange={handleChange}
            required
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px' }}
          />
        </div>
        
        {/* Celular */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="celular" style={{ marginBottom: '5px', fontWeight: '500' }}>Celular *</label>
          <input
            id="celular"
            type="text"
            name="celular"
            className="formulario-cliente-input"
            value={valores.celular}
            placeholder="11999999999 (apenas números)"
            onChange={e => {
              const onlyNums = e.target.value.replace(/\D/g, '');
              setValores((prev) => ({ ...prev, celular: onlyNums }));
            }}
            maxLength={11}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px' }}
          />
        </div>
        
        {/* Email */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="email" style={{ marginBottom: '5px', fontWeight: '500' }}>Email *</label>
          <input
            id="email"
            type="email"
            name="email"
            className="formulario-cliente-input"
            value={valores.email}
            placeholder="email@exemplo.com"
            onChange={handleChange}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px' }}
          />
        </div>
        
        {/* Data de Nascimento */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="data_nasc" style={{ marginBottom: '5px', fontWeight: '500' }}>Data de Nascimento</label>
          <input
            id="data_nasc"
            type="date"
            name="data_nasc"
            className="formulario-cliente-input"
            value={valores.data_nasc || ''}
            onChange={handleChange}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px' }}
          />
        </div>
        
        {/* Tipo de Pessoa */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ marginBottom: '10px', fontWeight: '500' }}>Tipo de Pessoa *</label>
          <div style={{ display: 'flex', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="tipoPessoaId"
                value="1"
                checked={valores.tipoPessoaId == 1}
                onChange={handleChange}
              />
              Pessoa Física (PF)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="tipoPessoaId"
                value="2"
                checked={valores.tipoPessoaId == 2}
                onChange={handleChange}
              />
              Pessoa Jurídica (PJ)
            </label>
          </div>
        </div>

{/* RG */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="rg" style={{ marginBottom: '5px', fontWeight: '500' }}>RG</label>
          <input
            id="rg"
            type="text"
            name="rg"
            className="formulario-cliente-input"
            value={valores.rg || ''}
            placeholder="Digite o RG"
            onChange={handleChange}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px' }}
          />
        </div>
        
        {/* CPF/CNPJ */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="cpF_CNPJ" style={{ marginBottom: '5px', fontWeight: '500' }}>
            {getCpfCnpjConfig().label} *
          </label>
          <input
            id="cpF_CNPJ"
            type="text"
            name="cpF_CNPJ"
            className="formulario-cliente-input"
            value={valores.cpF_CNPJ || ''}
            placeholder={getCpfCnpjConfig().placeholder}
            onChange={handleChange}
            onBlur={() => setErroCpfCnpj(mensagemErroDocumento(valores.cpF_CNPJ, getCpfCnpjConfig().label))}
            maxLength={getCpfCnpjConfig().maxLength}
            style={{ 
              padding: '8px', 
              borderRadius: '5px', 
              border: erroCpfCnpj ? '1px solid #f44336' : '1px solid #ccc', 
              outline: 'none', 
              fontSize: '14px' 
            }}
          />
          {erroCpfCnpj && (
            <small style={{ fontSize: '12px', color: '#f44336', marginTop: '2px' }}>
              {erroCpfCnpj}
            </small>
          )}
          <small style={{ 
            fontSize: '12px', 
            color: '#666', 
            marginTop: '2px' 
          }}>
            {getCpfCnpjConfig().label === 'CPF' ? 'Máximo 11 dígitos' : 'Máximo 14 dígitos'}
          </small>
        </div>

        {/* Registro Único */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="regUnico" style={{ marginBottom: '5px', fontWeight: '500' }}>Registro Único</label>
          <input
            id="regUnico"
            type="text"
            name="regUnico"
            className="formulario-cliente-input"
            value={valores.regUnico || ''}
            placeholder="Digite o Registro Único"
            onChange={handleChange}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px' }}
          />
        </div>
      </div>
      
      {/* Seção de Endereço */}
      <div style={{ width: '100%', textAlign: 'center', margin: '30px 0 20px 0', fontWeight: 'bold', color: '#666', fontSize: '20px' }}>
        Endereço
      </div>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {/* CEP */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="cep" style={{ marginBottom: '5px', fontWeight: '500' }}>CEP *</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              id="cep"
              type="text"
              name="cep"
              className="formulario-cliente-input"
              // Guarda só os 8 números; exibe 00000-000
              value={(valores.cep || '').replace(/^(\d{5})(\d)/, '$1-$2')}
              placeholder="00000-000"
              onChange={e => {
                // Tira pontuação antes de limitar (maxLength 8 cortava o último número de "06395-000")
                const onlyNums = e.target.value.replace(/\D/g, '').slice(0, 8);
                setValores((prev) => ({ ...prev, cep: onlyNums }));
                if (onlyNums.length === 8) {
                  buscarCep(onlyNums);
                }
              }}
              maxLength={10}
              required
              style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px', flex: 1 }}
            />
            {buscandoCep && (
              <span style={{ fontSize: '13px', color: 'rgb(235, 133, 0)', fontWeight: '500', whiteSpace: 'nowrap' }}>Buscando...</span>
            )}
          </div>
        </div>

        {/* Espaço vazio para alinhar grid */}
        <div></div>

        {/* Rua (Endereço) */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="endereco" style={{ marginBottom: '5px', fontWeight: '500' }}>Rua *</label>
          <input
            id="endereco"
            type="text"
            name="endereco"
            className="formulario-cliente-input"
            value={valores.endereco || ''}
            placeholder="Rua / Logradouro"
            onChange={handleChange}
            required
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px', backgroundColor: buscandoCep ? '#f5f5f5' : 'white' }}
          />
        </div>

        {/* Número */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="numero" style={{ marginBottom: '5px', fontWeight: '500' }}>Número *</label>
          <input
            id="numero"
            type="text"
            name="numero"
            className="formulario-cliente-input"
            value={valores.numero || ''}
            placeholder="Nº"
            onChange={handleChange}
            required
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px' }}
          />
        </div>
        
        {/* Bairro */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="bairro" style={{ marginBottom: '5px', fontWeight: '500' }}>Bairro *</label>
          <input
            id="bairro"
            type="text"
            name="bairro"
            className="formulario-cliente-input"
            value={valores.bairro || ''}
            placeholder="Bairro"
            onChange={handleChange}
            required
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px', backgroundColor: buscandoCep ? '#f5f5f5' : 'white' }}
          />
        </div>
        
        {/* Cidade */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="cidade" style={{ marginBottom: '5px', fontWeight: '500' }}>Cidade *</label>
          <input
            id="cidade"
            type="text"
            name="cidade"
            className="formulario-cliente-input"
            value={valores.cidade || ''}
            placeholder="Cidade"
            onChange={handleChange}
            required
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px', backgroundColor: buscandoCep ? '#f5f5f5' : 'white' }}
          />
        </div>
        
        {/* UF */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="uf" style={{ marginBottom: '5px', fontWeight: '500' }}>UF *</label>
          <select
            id="uf"
            name="uf"
            className="formulario-cliente-input"
            value={valores.uf || ''}
            onChange={handleChange}
            required
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', outline: 'none', fontSize: '14px', backgroundColor: buscandoCep ? '#f5f5f5' : 'white' }}
          >
            <option value="">Selecione</option>
            <option value="AC">AC</option>
            <option value="AL">AL</option>
            <option value="AP">AP</option>
            <option value="AM">AM</option>
            <option value="BA">BA</option>
            <option value="CE">CE</option>
            <option value="DF">DF</option>
            <option value="ES">ES</option>
            <option value="GO">GO</option>
            <option value="MA">MA</option>
            <option value="MT">MT</option>
            <option value="MS">MS</option>
            <option value="MG">MG</option>
            <option value="PA">PA</option>
            <option value="PB">PB</option>
            <option value="PR">PR</option>
            <option value="PE">PE</option>
            <option value="PI">PI</option>
            <option value="RJ">RJ</option>
            <option value="RN">RN</option>
            <option value="RS">RS</option>
            <option value="RO">RO</option>
            <option value="RR">RR</option>
            <option value="SC">SC</option>
            <option value="SP">SP</option>
            <option value="SE">SE</option>
            <option value="TO">TO</option>
          </select>
        </div>
      </div>
      
      <div className="formulario-cliente-botoes" style={{ marginTop: '30px', textAlign: 'center', display: 'flex', gap: '20px', justifyContent: 'center' }}>
        <button 
          type="submit" 
          style={{
            padding: '12px 40px',
            backgroundColor: 'rgb(235, 133, 0)',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            transition: 'background-color 0.3s ease',
            outline: 'none',
            boxShadow: 'none',
            opacity: botaoDesabilitado ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgb(208, 116, 0)';
            e.target.style.boxShadow = 'none';
            e.target.style.outline = 'none';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgb(235, 133, 0)';
            e.target.style.boxShadow = 'none';
            e.target.style.outline = 'none';
          }}
        >
          {botaoDesabilitado ? 'Aguarde 5 segundos...' : (valores.id ? 'Atualizar Cliente' : 'Criar Cliente')}
        </button>
        
        {onCancelar && (
          <button 
            type="button" 
            onClick={onCancelar} 
            style={{
              padding: '12px 40px',
              backgroundColor: '#424242',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'background-color 0.3s ease',
              outline: 'none',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#303030';
              e.target.style.boxShadow = 'none';
              e.target.style.outline = 'none';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#424242';
              e.target.style.boxShadow = 'none';
              e.target.style.outline = 'none';
            }}
          >
            Cancelar
          </button>
        )}
      </div>

      {/* Notificação de sucesso */}
      {mostrarSucesso && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'white',
          color: 'rgb(235, 133, 0)',
          padding: '15px 30px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '16px',
          fontWeight: '600',
          zIndex: 9999,
          animation: 'fadeInOut 2.5s ease-in-out forwards',
          border: '2px solid rgb(235, 133, 0)'
        }}>
          Cliente adicionado com sucesso!
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          10% {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          80% {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
        }
      `}</style>
    </form>
  );
}

export default FormularioCliente;
